import { Dialog, CheckBox, Button } from '@workspace/design-system/src/components';
import { useState } from 'react';
import type { PricingTier } from '@src/hooks/template/utils';

interface PricingTierModalProps {
    defaultTiers?: PricingTier[];
    onSave: (tiers: PricingTier[]) => void;
    onClose: () => void;
}

const ALL_TIER: PricingTier = 'ALL';
const OTHER_TIERS: PricingTier[] = ['PRO', 'ENTERPRISE', 'TEACHER', 'CLASS'];

export default function PricingTierModal({ defaultTiers = [], onSave, onClose }: PricingTierModalProps) {
    const [selectedTiers, setSelectedTiers] = useState<PricingTier[]>(defaultTiers);

    const isAllSelected = selectedTiers.includes(ALL_TIER);

    const handleTierToggle = (tier: PricingTier) => {
        setSelectedTiers((prev) => {
            // "모든 요금제 사용 가능" 선택 시
            if (tier === ALL_TIER) {
                if (prev.includes(ALL_TIER)) {
                    // 이미 선택되어 있으면 해제
                    return prev.filter((t) => t !== ALL_TIER);
                } else {
                    // 선택하면 다른 모든 옵션 해제하고 ALL만 선택
                    return [ALL_TIER];
                }
            } else {
                // 다른 옵션 선택 시
                if (prev.includes(ALL_TIER)) {
                    // ALL이 선택되어 있으면 ALL 해제하고 해당 옵션만 선택
                    return [tier];
                } else {
                    // ALL이 선택되지 않았으면 일반 토글
                    if (prev.includes(tier)) {
                        return prev.filter((t) => t !== tier);
                    } else {
                        return [...prev, tier];
                    }
                }
            }
        });
    };

    const handleSave = () => {
        onSave(selectedTiers);
        onClose();
    };

    return (
        <Dialog>
            <Dialog.Content className="max-w-[480px]">
                <Dialog.HiddenTitle>사용 가능 요금제 설정</Dialog.HiddenTitle>
                <div className="p-6 flex flex-col gap-4">
                    <div className="flex items-center gap-1 w-full">
                        <h2 className="text-size-3 font-bold leading-font-height-3 text-text-default flex-1">
                            사용 가능 요금제 설정
                        </h2>
                    </div>

                    <div className="flex flex-col gap-2 w-full">
                        <p className="text-size-6 font-regular leading-font-height-5 text-text-default tracking-[-0.1px]">
                            '모든 요금제 사용 가능'을 선택하면 다른 요금제 옵션은 선택할 수 없습니다.
                        </p>
                    </div>

                    <div className="flex flex-col gap-4">
                        <CheckBox
                            label="모든 요금제 사용 가능"
                            size="32"
                            checked={isAllSelected}
                            onChange={() => handleTierToggle(ALL_TIER)}
                        />
                        <CheckBox
                            label="프로"
                            size="32"
                            checked={selectedTiers.includes('PRO')}
                            disabled={isAllSelected}
                            onChange={() => handleTierToggle('PRO')}
                        />
                        <CheckBox
                            label="엔터프라이즈"
                            size="32"
                            checked={selectedTiers.includes('ENTERPRISE')}
                            disabled={isAllSelected}
                            onChange={() => handleTierToggle('ENTERPRISE')}
                        />
                        <CheckBox
                            label="티처"
                            size="32"
                            checked={selectedTiers.includes('TEACHER')}
                            disabled={isAllSelected}
                            onChange={() => handleTierToggle('TEACHER')}
                        />
                        <CheckBox
                            label="클래스"
                            size="32"
                            checked={selectedTiers.includes('CLASS')}
                            disabled={isAllSelected}
                            onChange={() => handleTierToggle('CLASS')}
                        />
                    </div>
                </div>

                <Dialog.Footer>
                    <Button styleType="basic" styleSize="M-40" onClick={onClose}>
                        닫기
                    </Button>
                    <Button styleType="primary" styleSize="M-40" onClick={handleSave}>
                        저장
                    </Button>
                </Dialog.Footer>
            </Dialog.Content>
        </Dialog>
    );
}

